import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Abas,
  Alerta,
  Bloco,
  HeroModulo,
  Campo,
  CAMPO_SIM,
  Casos,
  type CasoPratico,
  Linha,
  Referencias,
  Tabela,
  copiar,
} from "../componentes/HubDpUi";
import { reais, type EntradaHolerite, type ParametrosFolha } from "../lib/folhaDp";
import { dataBr, gerarCompetencia, lerCompetencia, type CategoriaEvento } from "../lib/esocialDp";
import { eventoXml, resumoGuias, type TipoEventoXml } from "../lib/documentosDp";
import { mascararMoeda, mascararInteiro, mascararCnpj, mascararCpf, moedaDeNumero } from "../lib/mascaras";
import { REFERENCIA_ESOCIAL, REJEICOES } from "../componentes/HubDpTextos";

// O módulo eSocial: o cronograma de uma competência, o diagnóstico das rejeições
// e o esqueleto XML dos três eventos principais.
//
// A VERSÃO DO LEIAUTE é campo, não constante: na origem estava cravada no
// componente (`v_S_01_03_00`), e leiaute do eSocial muda por Nota Técnica — uma
// versão cravada num `.tsx` é a ferramenta ensinando estrutura obsoleta sem
// ninguém notar. Aqui ela é editável e a tela mostra qual está em uso.

const BASE = "/ferramenta/hub-dp/esocial";
const ABAS = [
  { slug: "", rotulo: "Cronograma" },
  { slug: "rejeicoes", rotulo: "Rejeições" },
  { slug: "xml", rotulo: "Gerar XML" },
  { slug: "referencia", rotulo: "Referência" },
  { slug: "casos", rotulo: "Casos práticos" },
];

interface Form {
  competencia: string;
  /** Salários dos empregados, um por linha do formulário. */
  salarios: string[];
  ratPercentual: string;
  fap: string;
  terceirosPercentual: string;
  regimeCprb: boolean;
  receitaBruta: string;
  cprbPercentual: string;
  admissoes: string;
  desligamentos: string;
  afastamentos: string;
  alteracoesCadastrais: string;
  alteracoesContratuais: string;
  temReinf: boolean;
  // XML
  tipoXml: TipoEventoXml;
  versaoLeiaute: string;
  cnpjEmpregador: string;
  cpfTrabalhador: string;
  nomeTrabalhador: string;
  matricula: string;
  cargo: string;
  salarioXml: string;
}

const VAZIO: Form = {
  competencia: "",
  salarios: [""],
  ratPercentual: "2",
  fap: "1,0",
  terceirosPercentual: "5,8",
  regimeCprb: false,
  receitaBruta: "",
  cprbPercentual: "4,5",
  admissoes: "0",
  desligamentos: "0",
  afastamentos: "0",
  alteracoesCadastrais: "0",
  alteracoesContratuais: "0",
  temReinf: false,
  tipoXml: "S-2200",
  versaoLeiaute: "v_S_01_03_00",
  cnpjEmpregador: "",
  cpfTrabalhador: "",
  nomeTrabalhador: "",
  matricula: "001",
  cargo: "",
  salarioXml: "",
};

const num = (t: string): number => mascararMoeda(t).numero ?? 0;
const int = (t: string): number => Number(t.replace(/\D+/g, "")) || 0;
const pct = (t: string): number => Number(t.replace(",", ".").replace(/[^0-9.]/g, "")) || 0;
const mascararPct = (t: string): string => {
  const limpo = t.replace(/[^0-9,]/g, "").replace(/,+/g, ",");
  const [i = "", c = ""] = limpo.split(",");
  return limpo.includes(",") ? `${i.slice(0, 2)},${c.slice(0, 2)}` : i.slice(0, 3);
};

const funcionario = (salario: number): EntradaHolerite => ({
  salarioBase: salario,
  horasExtras: 0,
  horasNoturnas: 0,
  periculosidade: false,
  insalubridade: "nenhuma",
  faltas: 0,
  valeTransporte: false,
  dependentes: 0,
  modeloIrrf: "completo",
});

const CASOS: CasoPratico<Partial<Form>>[] = [
  {
    nome: "Competência tranquila, 3 empregados",
    descricao: "Nenhuma admissão nem desligamento. O mínimo que se transmite todo mês.",
    valores: {
      competencia: "2026-03",
      salarios: [moedaDeNumero(3000), moedaDeNumero(2500), moedaDeNumero(4200)],
    },
  },
  {
    nome: "Mês com admissão e desligamento",
    descricao:
      "4 empregados, 1 admissão e 2 desligamentos. Repare que o S-2200 não tem data de calendário: o prazo é do FATO.",
    valores: {
      competencia: "2026-03",
      salarios: [moedaDeNumero(3000), moedaDeNumero(2500), moedaDeNumero(4200), moedaDeNumero(1800)],
      admissoes: "1",
      desligamentos: "2",
      alteracoesContratuais: "1",
    },
  },
  {
    nome: "Empresa na CPRB, com Reinf",
    descricao:
      "3 empregados, receita de R$ 200.000. A CPRB acrescenta o S-1280 ao cronograma, e a Reinf traz o R-2010 e o R-2099.",
    valores: {
      competencia: "2026-03",
      salarios: [moedaDeNumero(5000), moedaDeNumero(6000), moedaDeNumero(4000)],
      regimeCprb: true,
      receitaBruta: moedaDeNumero(200000),
      temReinf: true,
    },
  },
  {
    nome: "Vencimento que cai em domingo",
    descricao:
      "Competência 08/2026: o dia 20 de setembro é domingo, e o prazo RECUA para a sexta — nunca adia.",
    valores: { competencia: "2026-08", salarios: [moedaDeNumero(3000)] },
  },
  {
    nome: "Competência sem movimento",
    descricao:
      "Nenhum empregado. Os 16 eventos continuam na lista, cada um dizendo por que não se aplica — é a diferença entre 'não preciso enviar' e 'esqueci que existe'.",
    valores: { competencia: "2026-03", salarios: [] },
  },
];

const NOME_CATEGORIA: Record<CategoriaEvento, string> = {
  tabela: "Tabelas",
  "nao-periodico": "Não-periódicos",
  periodico: "Periódicos",
  reinf: "EFD-Reinf",
};

export default function DpEsocial({ p, aba }: { p: ParametrosFolha; aba: string }) {
  const [f, setF] = useState<Form>(VAZIO);
  const [copiado, setCopiado] = useState<string | null>(null);
  const navegar = useNavigate();
  const troca = <K extends keyof Form>(c: K, v: Form[K]) => setF((a) => ({ ...a, [c]: v }));

  const competenciaValida = lerCompetencia(f.competencia) !== null;
  const salarios = f.salarios.map(num).filter((s) => s > 0);
  const r = competenciaValida
    ? gerarCompetencia(
        {
          competencia: f.competencia,
          funcionarios: salarios.map(funcionario),
          encargos: {
            receitaBruta: num(f.receitaBruta),
            regime: f.regimeCprb ? "cprb" : "folha",
            cprbPercentual: pct(f.cprbPercentual),
            ratPercentual: pct(f.ratPercentual),
            fap: pct(f.fap),
            terceirosPercentual: pct(f.terceirosPercentual),
            incluirFgts: true,
          },
          admissoes: int(f.admissoes),
          desligamentos: int(f.desligamentos),
          afastamentos: int(f.afastamentos),
          alteracoesCadastrais: int(f.alteracoesCadastrais),
          alteracoesContratuais: int(f.alteracoesContratuais),
          temReinf: f.temReinf,
        },
        p,
      )
    : null;

  return (
    <>
      <HeroModulo
        titulo="eSocial"
        subtitulo="O cronograma de uma competência, as rejeições mais comuns e o esqueleto dos eventos."
        heroi={
          r
            ? {
                rotulo: `Eventos a enviar em ${r.nome}`,
                valor: String(r.eventos.reduce((s, e) => s + e.quantidade, 0)),
                detalhe: r.vencimentoPeriodicos
                  ? `os periódicos vencem em ${dataBr(r.vencimentoPeriodicos)}`
                  : undefined,
              }
            : null
        }
        vazio="Informe a competência (AAAA-MM) para montar o cronograma."
      />
      <Abas base={BASE} abas={ABAS} />

      {aba === "" && (
        <div className="space-y-6">
          <form id="dados-da-competencia" className="surface grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Competência" ajuda="No formato AAAA-MM, por exemplo 2026-03.">
              <input
                className={CAMPO_SIM}
                placeholder="2026-03"
                value={f.competencia}
                onChange={(e) => troca("competencia", e.target.value.slice(0, 7))}
              />
            </Campo>
            <Campo rotulo="Eventos do mês" ajuda="Quantos de cada aconteceram na competência.">
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    ["admissoes", "Admissões"],
                    ["desligamentos", "Desligamentos"],
                    ["afastamentos", "Afastamentos"],
                    ["alteracoesContratuais", "Alt. contratuais"],
                  ] as const
                ).map(([campo, rotulo]) => (
                  <label key={campo} className="text-xs text-cinza">
                    {rotulo}
                    <input
                      className={`${CAMPO_SIM} mt-0.5 h-9`}
                      inputMode="numeric"
                      value={f[campo]}
                      onChange={(e) => troca(campo, mascararInteiro(e.target.value, 3))}
                    />
                  </label>
                ))}
              </div>
            </Campo>

            <fieldset className="sm:col-span-2">
              <legend className="mb-1.5 text-xs font-semibold text-cinza">
                Salários dos empregados
              </legend>
              <div className="grid gap-2 sm:grid-cols-3">
                {f.salarios.map((s, i) => (
                  <input
                    key={i}
                    className={CAMPO_SIM}
                    inputMode="numeric"
                    placeholder={`Empregado ${i + 1}`}
                    aria-label={`Salário do empregado ${i + 1}`}
                    value={s}
                    onChange={(e) => {
                      const novos = [...f.salarios];
                      novos[i] = mascararMoeda(e.target.value).texto;
                      troca("salarios", novos);
                    }}
                  />
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className="rounded-full border border-borda px-3 py-1.5 text-xs font-semibold text-titulo"
                  onClick={() => troca("salarios", [...f.salarios, ""])}
                >
                  + Empregado
                </button>
                {f.salarios.length > 1 && (
                  <button
                    type="button"
                    className="rounded-full border border-borda px-3 py-1.5 text-xs font-semibold text-titulo"
                    onClick={() => troca("salarios", f.salarios.slice(0, -1))}
                  >
                    − Remover o último
                  </button>
                )}
              </div>
            </fieldset>

            <Campo rotulo="RAT (%)">
              <select className={CAMPO_SIM} value={f.ratPercentual} onChange={(e) => troca("ratPercentual", e.target.value)}>
                <option value="1">1 — risco leve</option>
                <option value="2">2 — risco médio</option>
                <option value="3">3 — risco grave</option>
              </select>
            </Campo>
            <Campo rotulo="FAP">
              <input
                className={CAMPO_SIM}
                inputMode="decimal"
                value={f.fap}
                onChange={(e) => troca("fap", mascararPct(e.target.value))}
              />
            </Campo>

            <label className="flex items-center gap-2.5 text-sm text-tinta sm:col-span-2">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={f.regimeCprb}
                onChange={(e) => troca("regimeCprb", e.target.checked)}
              />
              Empresa na CPRB (acrescenta o S-1280 ao cronograma)
            </label>
            {f.regimeCprb && (
              <Campo rotulo="Receita bruta do mês">
                <input
                  className={CAMPO_SIM}
                  inputMode="numeric"
                  placeholder="R$ 0,00"
                  value={f.receitaBruta}
                  onChange={(e) => troca("receitaBruta", mascararMoeda(e.target.value).texto)}
                />
              </Campo>
            )}
            <label className="flex items-center gap-2.5 text-sm text-tinta sm:col-span-2">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={f.temReinf}
                onChange={(e) => troca("temReinf", e.target.checked)}
              />
              A empresa toma serviços com retenção (acrescenta o R-2010 e o R-2099)
            </label>
          </form>

          {!competenciaValida ? (
            <Alerta tom="atencao">
              Informe a competência no formato <strong>AAAA-MM</strong> — por exemplo 2026-03 — para
              montar o cronograma.
            </Alerta>
          ) : (
            r && (
              <div id="resultado" className="space-y-5">
                <Bloco titulo={`Apuração de ${r.nome}`} destaque>
                  <Linha rotulo="Trabalhadores com remuneração" valor={String(r.holerites.length)} />
                  <Linha rotulo="Folha bruta" valor={reais(r.folhaBruta)} />
                  <Linha rotulo="INSS retido dos empregados" valor={reais(r.totalInss)} />
                  <Linha rotulo="IRRF retido" valor={reais(r.totalIrrf)} />
                  <Linha rotulo="Encargos do empregador" valor={reais(r.encargos.total)} />
                  <Linha rotulo="Custo total da competência" valor={reais(r.custoTotal)} tom="total" />
                </Bloco>

                <Bloco titulo="Prazos">
                  <Linha
                    rotulo="Eventos periódicos (S-1200 / S-1210 / S-1299)"
                    valor={r.vencimentoPeriodicos ? dataBr(r.vencimentoPeriodicos) : "—"}
                  />
                  <Linha
                    rotulo="DCTFWeb e DARF previdenciário"
                    valor={r.vencimentoDctfweb ? dataBr(r.vencimentoDctfweb) : "—"}
                  />
                  <Linha rotulo="FGTS Digital" valor={r.vencimentoFgts ? dataBr(r.vencimentoFgts) : "—"} />
                  {r.semFeriados && (
                    <p className="mt-2 text-xs leading-relaxed text-alerta-tinta">
                      As datas recuam de sábado e domingo, mas <strong>não consideram feriados</strong>.
                      Confira no calendário oficial se cair perto de um.
                    </p>
                  )}
                </Bloco>

                <section>
                  <h3 className="mb-2 text-sm font-bold text-titulo">
                    O cronograma — os 16 eventos, na ordem de transmissão
                  </h3>
                  <p className="mb-3 text-xs leading-relaxed text-cinza">
                    Os eventos com quantidade zero <strong>continuam na lista</strong>, com o motivo.
                    É a diferença entre "não preciso enviar este mês" e "esqueci que existe" — e a
                    segunda é o que faz a empresa tomar multa.
                  </p>
                  <div className="overflow-x-auto rounded-cf border border-borda">
                    <table className="w-full min-w-max border-collapse text-sm">
                      <thead>
                        <tr className="bg-superficie-2">
                          {["Código", "Evento", "Qtd.", "Vencimento", "Situação"].map((c) => (
                            <th key={c} scope="col" className="px-3 py-2 text-left text-xs font-semibold tracking-wide text-cinza uppercase">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(["tabela", "nao-periodico", "periodico", "reinf"] as CategoriaEvento[]).map((cat) => {
                          const daCategoria = r.eventos.filter((e) => e.definicao.categoria === cat);
                          return (
                            <>
                              <tr key={cat} className="bg-chip">
                                <td colSpan={5} className="px-3 py-1.5 text-xs font-bold tracking-wide text-titulo uppercase">
                                  {NOME_CATEGORIA[cat]}
                                </td>
                              </tr>
                              {daCategoria.map((e) => (
                                <tr key={e.definicao.codigo} className={e.quantidade > 0 ? undefined : "opacity-60"}>
                                  <td className="border-t border-borda px-3 py-2 font-semibold text-titulo">
                                    {e.definicao.codigo}
                                  </td>
                                  <td className="border-t border-borda px-3 py-2 text-tinta">
                                    {e.definicao.nome}
                                    <span className="block text-xs text-cinza">{e.definicao.prazo}</span>
                                  </td>
                                  <td className="border-t border-borda px-3 py-2 text-center tabular-nums text-tinta">
                                    {e.quantidade}
                                  </td>
                                  <td className="border-t border-borda px-3 py-2 whitespace-nowrap text-tinta tabular-nums">
                                    {e.vencimento ? dataBr(e.vencimento) : "prazo do fato"}
                                  </td>
                                  <td className="border-t border-borda px-3 py-2 text-xs text-cinza">{e.motivo}</td>
                                </tr>
                              ))}
                            </>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>

                <button
                  type="button"
                  className="pill-cta"
                  onClick={async () =>
                    setCopiado(
                      (await copiar(
                        resumoGuias({
                          competencia: r.nome,
                          folhaBruta: reais(r.folhaBruta),
                          inssRetido: reais(r.totalInss),
                          inssPatronal: reais(r.encargos.inssPatronal),
                          rat: reais(r.encargos.rat),
                          terceiros: reais(r.encargos.terceiros),
                          fgts: reais(r.encargos.fgts),
                          irrfRetido: reais(r.totalIrrf),
                          totalEncargos: reais(r.encargos.total),
                          custoTotal: reais(r.custoTotal),
                          vencimentoDctfweb: r.vencimentoDctfweb ? dataBr(r.vencimentoDctfweb) : "—",
                          vencimentoFgts: r.vencimentoFgts ? dataBr(r.vencimentoFgts) : "—",
                          vencimentoPeriodicos: r.vencimentoPeriodicos ? dataBr(r.vencimentoPeriodicos) : "—",
                          regime: f.regimeCprb ? "CPRB" : "Folha (20%)",
                          semFeriados: r.semFeriados,
                        }),
                      ))
                        ? "guias"
                        : null,
                    )
                  }
                >
                  {copiado === "guias" ? "Copiado" : "Copiar o resumo de guias"}
                </button>
                <p className="text-xs leading-relaxed text-cinza">
                  O resumo sai em texto, para colar numa planilha ou num e-mail — não em folha
                  imprimível, porque o destino dele é outro sistema.
                </p>
              </div>
            )
          )}
        </div>
      )}

      {aba === "rejeicoes" && (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-cinza">
            As dez rejeições mais frequentes do Ambiente Nacional, com a causa e o que fazer. Boa
            parte delas é consequência de <strong>transmitir fora de ordem</strong> — veja a aba
            Referência.
          </p>
          {REJEICOES.map((rj) => (
            <details key={rj.codigo} className="rounded-cf border border-borda bg-superficie p-4">
              <summary className="cursor-pointer text-sm font-bold text-titulo">
                <span className="mr-2 rounded bg-chip px-1.5 py-0.5 text-xs text-chip-tinta">{rj.codigo}</span>
                {rj.titulo}
                <span className="ml-2 text-xs font-normal text-cinza">({rj.evento})</span>
              </summary>
              <div className="mt-3 space-y-2 text-sm leading-relaxed">
                <p>
                  <strong className="text-titulo">Causa.</strong> {rj.causa}
                </p>
                <p>
                  <strong className="text-titulo">O que fazer.</strong> {rj.solucao}
                </p>
              </div>
            </details>
          ))}
        </div>
      )}

      {aba === "xml" && (
        <div className="space-y-6">
          <Alerta tom="atencao" titulo="Esqueleto didático, ambiente de testes">
            O XML sai com <code>tpAmb=2</code> (testes) e não é configurável. Serve para você
            reconhecer a estrutura do evento — para envio real, use o portal ou o web service
            oficial, com assinatura ICP-Brasil.
          </Alerta>

          <form id="dados-do-xml" className="surface grid gap-4 p-5 sm:grid-cols-2 sm:p-6" onSubmit={(e) => e.preventDefault()}>
            <Campo rotulo="Evento">
              <select
                className={CAMPO_SIM}
                value={f.tipoXml}
                onChange={(e) => troca("tipoXml", e.target.value as TipoEventoXml)}
              >
                <option value="S-2200">S-2200 — Admissão</option>
                <option value="S-2299">S-2299 — Desligamento</option>
                <option value="S-1200">S-1200 — Remuneração</option>
              </select>
            </Campo>
            <Campo
              rotulo="Versão do leiaute"
              ajuda="Muda por Nota Técnica. É campo, e não valor cravado no código, para a ferramenta não ensinar estrutura obsoleta."
            >
              <input
                className={CAMPO_SIM}
                value={f.versaoLeiaute}
                onChange={(e) => troca("versaoLeiaute", e.target.value)}
              />
            </Campo>
            <Campo rotulo="CNPJ do empregador">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={f.cnpjEmpregador}
                onChange={(e) => troca("cnpjEmpregador", mascararCnpj(e.target.value))}
              />
            </Campo>
            <Campo rotulo="CPF do trabalhador">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={f.cpfTrabalhador}
                onChange={(e) => troca("cpfTrabalhador", mascararCpf(e.target.value))}
              />
            </Campo>
            <Campo rotulo="Nome do trabalhador">
              <input
                className={CAMPO_SIM}
                value={f.nomeTrabalhador}
                onChange={(e) => troca("nomeTrabalhador", e.target.value)}
              />
            </Campo>
            <Campo rotulo="Matrícula" ajuda="É chave para o eSocial: '001' e '1' são vínculos diferentes.">
              <input className={CAMPO_SIM} value={f.matricula} onChange={(e) => troca("matricula", e.target.value)} />
            </Campo>
            <Campo rotulo="Cargo">
              <input className={CAMPO_SIM} value={f.cargo} onChange={(e) => troca("cargo", e.target.value)} />
            </Campo>
            <Campo rotulo="Salário">
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={f.salarioXml}
                onChange={(e) => troca("salarioXml", mascararMoeda(e.target.value).texto)}
              />
            </Campo>
          </form>

          <XmlGerado f={f} copiado={copiado === "xml"} setCopiado={(v) => setCopiado(v ? "xml" : null)} />
        </div>
      )}

      {aba === "referencia" && (
        <div className="space-y-6">
          {REFERENCIA_ESOCIAL.map((t) => (
            <Tabela key={t.titulo} tabela={t} />
          ))}
          <Alerta tom="informacao" titulo="Por que a ordem importa">
            Tabela antes de quem a referencia; não-periódicos antes dos periódicos; o S-1299 por
            último. Fora dessa ordem o Ambiente Nacional rejeita com o código 301 ou 312 — as duas
            estão na aba Rejeições.
          </Alerta>
          <Alerta tom="atencao" titulo="Sem o S-1299 nada é apurado">
            O fechamento é o que gera a DCTFWeb. Enviar o S-1200 e esquecer o fechamento deixa a
            competência aberta e a guia não sai — e o prazo do dia 20 corre igual.
          </Alerta>
        </div>
      )}

      {aba === "casos" && (
        <Casos
          casos={CASOS}
          aoCarregar={(v) => {
            setF({ ...VAZIO, ...v, salarios: v.salarios ?? [] });
            navegar(BASE);
          }}
        />
      )}

      <Referencias inss={p.referenciaInss} irrf={p.referenciaIrrf} />
    </>
  );
}

function XmlGerado({
  f,
  copiado,
  setCopiado,
}: {
  f: Form;
  copiado: boolean;
  setCopiado: (v: boolean) => void;
}) {
  // O id do evento tem forma fixa no eSocial: ID + tipo de inscrição + CNPJ +
  // timestamp + sequencial. Montado aqui, na TELA, porque depende do relógio — os
  // documentos são puros e não leem `new Date()`.
  const agora = new Date();
  const carimbo =
    `${agora.getFullYear()}${String(agora.getMonth() + 1).padStart(2, "0")}` +
    `${String(agora.getDate()).padStart(2, "0")}${String(agora.getHours()).padStart(2, "0")}` +
    `${String(agora.getMinutes()).padStart(2, "0")}${String(agora.getSeconds()).padStart(2, "0")}`;
  const cnpj = f.cnpjEmpregador.replace(/\D+/g, "").padEnd(14, "0");
  const idEvento = `ID1${cnpj}${carimbo}00001`;

  const xml = eventoXml({
    tipo: f.tipoXml,
    versaoLeiaute: f.versaoLeiaute,
    cnpjEmpregador: f.cnpjEmpregador,
    cpfTrabalhador: f.cpfTrabalhador,
    nomeTrabalhador: f.nomeTrabalhador,
    nascimento: "1990-01-01",
    matricula: f.matricula,
    admissao: `${new Date().getFullYear()}-01-02`,
    desligamento: `${new Date().getFullYear()}-12-20`,
    motivoDesligamento: "02",
    competencia: f.competencia || `${new Date().getFullYear()}-01`,
    cargo: f.cargo,
    salario: String((mascararMoeda(f.salarioXml).numero ?? 0).toFixed(2)),
    idEvento,
  });

  return (
    <div className="space-y-3">
      <pre
        id="xml-do-evento"
        className="max-h-[60vh] overflow-auto rounded-cf border border-borda bg-superficie-2 p-4 text-xs leading-relaxed text-tinta"
      >
        {xml}
      </pre>
      <button
        type="button"
        className="pill-cta"
        onClick={async () => setCopiado(await copiar(xml))}
      >
        {copiado ? "Copiado" : "Copiar o XML"}
      </button>
      <p className="text-xs leading-relaxed text-cinza">
        Sai em texto, não em folha imprimível: o XML existe para ser colado num validador.
      </p>
    </div>
  );
}
